import { Link } from 'react-router-dom';
import { useCart } from '@/contexts/CartContext';
import { getShippingMethodLabel, hasToteBag } from '@/utils/shipping';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

export default function CartPage() {
  const { items, removeItem, updateQuantity, subtotal } = useCart();
  const slugs = items.map(i => i.product.slug);
  const methodLabel = getShippingMethodLabel(slugs);
  const shippingNote = '送料はチェックアウト時に配送先に応じて計算されます';

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main className="pt-16">
        <div className="bg-secondary-50 py-16 px-6 text-center">
          <p className="text-xs tracking-[0.3em] text-primary-500 mb-3 uppercase"
            style={{ fontFamily: "var(--font-heading)" }}>
            Cart
          </p>
          <h1 className="text-4xl md:text-6xl font-light text-foreground-950"
            style={{ fontFamily: "var(--font-heading)" }}>
            Your Cart
          </h1>
        </div>

        <div className="max-w-5xl mx-auto px-6 py-14">
          {items.length === 0 ? (
            <div className="text-center py-24">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-5 text-foreground-300">
                <i className="ri-shopping-bag-line text-4xl"></i>
              </div>
              <p className="text-foreground-500 mb-8 text-base" style={{ fontFamily: "var(--font-body)" }}>
                カートに商品がありません
              </p>
              <Link
                to="/products"
                className="inline-flex items-center gap-3 bg-primary-500 text-white px-10 py-4 text-sm tracking-[0.15em] hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer rounded-full"
                style={{ fontFamily: "var(--font-heading)" }}
              >
                Shop Now <i className="ri-arrow-right-up-line"></i>
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
              <div className="lg:col-span-2">
                <div className="space-y-6">
                  {items.map(item => (
                    <div key={item.product.slug + (item.selectedSize ?? '')} className="flex gap-5 pb-6 border-b border-background-200">
                      <div className="w-28 h-28 flex-shrink-0 rounded-xl overflow-hidden bg-background-100">
                        <img src={item.product.imageUrl ?? ''} alt={item.product.name} className="w-full h-full object-contain object-center" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-3">
                          <h3 className="text-base text-foreground-900 leading-snug mb-1"
                            style={{ fontFamily: "var(--font-body)" }}>
                            {item.product.name}
                            {item.selectedSize && (
                              <span className="text-foreground-500 text-sm ml-1">({item.selectedSize})</span>
                            )}
                          </h3>
                          <button
                            onClick={() => removeItem(item.product.slug, item.selectedSize)}
                            className="w-7 h-7 flex items-center justify-center text-foreground-300 hover:text-primary-500 transition-colors cursor-pointer flex-shrink-0"
                          >
                            <i className="ri-close-line"></i>
                          </button>
                        </div>
                        <div className="flex items-center justify-between mt-3">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => updateQuantity(item.product.slug, item.quantity - 1, item.selectedSize)}
                              className="w-8 h-8 flex items-center justify-center border border-background-300 hover:border-primary-400 transition-colors cursor-pointer rounded-full"
                            >
                              <i className="ri-subtract-line text-xs text-foreground-700"></i>
                            </button>
                            <span className="w-8 text-center text-base text-foreground-950"
                              style={{ fontFamily: "var(--font-heading)" }}>
                              {item.quantity}
                            </span>
                            <button
                              onClick={() => updateQuantity(item.product.slug, item.quantity + 1, item.selectedSize)}
                              className="w-8 h-8 flex items-center justify-center border border-background-300 hover:border-primary-400 transition-colors cursor-pointer rounded-full"
                            >
                              <i className="ri-add-line text-xs text-foreground-700"></i>
                            </button>
                          </div>
                          <span className="text-base text-foreground-950"
                            style={{ fontFamily: "var(--font-heading)" }}>
                            ¥{(item.product.price * item.quantity).toLocaleString()}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="lg:col-span-1">
                <div className="bg-secondary-50 rounded-xl p-6 sticky top-24">
                  <h2 className="text-xl font-light text-foreground-950 mb-6"
                    style={{ fontFamily: "var(--font-heading)" }}>
                    Order Summary
                  </h2>
                  <div className="space-y-3 mb-6">
                    <div className="flex justify-between text-sm">
                      <span className="text-foreground-600" style={{ fontFamily: "var(--font-body)" }}>小計</span>
                      <span className="text-foreground-900" style={{ fontFamily: "var(--font-heading)" }}>¥{subtotal.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-foreground-600" style={{ fontFamily: "var(--font-body)" }}>配送方法</span>
                      <span className="text-foreground-900 text-xs" style={{ fontFamily: "var(--font-heading)" }}>{methodLabel}</span>
                    </div>
                    <div className="border-t border-background-300 pt-3">
                      <p className="text-xs text-foreground-500 leading-relaxed" style={{ fontFamily: "var(--font-body)" }}>
                        <i className="ri-information-line mr-1"></i>
                        {shippingNote}
                      </p>
                    </div>
                    <div className="border-t border-background-300 pt-3 flex justify-between">
                      <span className="text-base text-foreground-950" style={{ fontFamily: "var(--font-body)" }}>小計</span>
                      <span className="text-xl text-foreground-950" style={{ fontFamily: "var(--font-heading)" }}>¥{subtotal.toLocaleString()}</span>
                    </div>
                  </div>
                  <Link
                    to="/checkout"
                    className="block w-full text-center bg-primary-500 text-white py-4 text-sm tracking-[0.15em] hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer rounded-full"
                    style={{ fontFamily: "var(--font-heading)" }}
                  >
                    Checkout <i className="ri-arrow-right-line"></i>
                  </Link>
                  <Link
                    to="/products"
                    className="block w-full text-center text-foreground-500 py-4 text-sm tracking-[0.1em] hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer"
                    style={{ fontFamily: "var(--font-heading)" }}
                  >
                    Continue Shopping
                  </Link>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
